import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, useWishlist } from "../lib/store";
import { ProductCard } from "../components/ProductCard";
import { SectionHead } from "./Home";
import { daysLeft, isExpiringSoon } from "../components/ExpiringVoucherBanner";
import { OrderDocuments } from "../components/OrderDocuments";

const ORDER_STATUS_IT = {
  "awaiting-transfer": "In attesa bonifico", pending: "In attesa", confirmed: "Pagato", "deposit-paid": "Acconto pagato",
  "balance-requested": "Saldo richiesto", "balance-paid": "Saldo pagato", shipped: "Spedito", delivered: "Consegnato",
  cancelled: "Annullato", "returned-voucher": "Reso — buono emesso",
};
import { Package, Heart, MapPin, User as UserIcon, Ticket } from "lucide-react";

const TABS = [
  { v: "orders", l: "I miei ordini", icon: <Package size={14} /> },
  { v: "vouchers", l: "Buoni negozio", icon: <Ticket size={14} /> },
  { v: "wishlist", l: "Wishlist", icon: <Heart size={14} /> },
  { v: "addresses", l: "Indirizzi", icon: <MapPin size={14} /> },
  { v: "profile", l: "Profilo", icon: <UserIcon size={14} /> },
];

export default function Account() {
  const { user, loading } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "orders");
  const [orders, setOrders] = useState([]);
  const [wl, setWl] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const { ids } = useWishlist();

  useEffect(() => {
    if (!user) return;
    if (tab === "vouchers") api.get("/vouchers/mine").then(({ data }) => setVouchers(data)).catch(() => {});
    if (tab === "orders") api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    if (tab === "wishlist") {
      if (ids.length) {
        api.get("/products", { params: { limit: 200 } }).then(({ data }) => setWl(data.filter((p) => ids.includes(p.product_id))));
      } else setWl([]);
    }
  }, [tab, user, ids]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-[#9CA3AF]">Caricamento…</div>;
  if (!user) return <Navigate to="/" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 md:py-14">
      <SectionHead eyebrow="Account" title={`Ciao, ${user.name?.split(" ")[0] || "collezionista"}`} subtitle={user.email} />

      <div className="mt-8 flex flex-wrap gap-2 border-b border-[#C9A227]/15 pb-3">
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`account-tab-${t.v}`}
            onClick={() => { setTab(t.v); setSp({ tab: t.v }); }}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-mono-ps uppercase tracking-widest border ${
              tab === t.v ? "border-[#C9A227] text-[#C9A227] bg-[#C9A227]/5" : "border-[#C9A227]/15 text-white/70 hover:text-[#C9A227]"
            } rounded-sm transition-colors`}
          >
            {t.icon}{t.l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "orders" && (
          <div className="space-y-3">
            {orders.length === 0 && <Empty text="Nessun ordine ancora." />}
            {orders.map((o) => (
              <div key={o.order_id} data-testid={`order-${o.order_id}`} className="border border-[#C9A227]/15 rounded-sm p-5 bg-[#0b0b0d]">
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <div className="eyebrow">#{o.order_id.slice(-8).toUpperCase()}</div>
                    <div className="font-display text-lg text-white mt-1">{o.items.length} articoli</div>
                    <div className="text-xs text-[#9CA3AF] mt-1">{new Date(o.created_at).toLocaleString("it-IT")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-ps text-xl font-bold text-[#C9A227]">€ {o.total.toFixed(2)}</div>
                    {o.voucher_discount > 0 && (
                      <div className="text-xs text-emerald-400 font-mono-ps mt-1">Buono: − € {o.voucher_discount.toFixed(2)}</div>
                    )}
                    <div className={`text-xs uppercase tracking-widest font-mono-ps mt-1 ${["cancelled"].includes(o.status) ? "text-[#DC2626]" : ["awaiting-transfer", "pending", "balance-requested"].includes(o.status) ? "text-[#D97706]" : "text-green-500"}`}>{ORDER_STATUS_IT[o.status] || o.status}</div>
                    {o.balance_due > 0 && (
                      <div className="text-xs text-[#C9A227] font-mono-ps mt-1">Saldo dovuto: € {o.balance_due.toFixed(2)}</div>
                    )}
                  </div>
                </div>
                <div className="mt-4 space-y-1 text-sm text-white/80">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex justify-between">
                      <span className="truncate pr-3">
                        {it.name} <span className="text-[#9CA3AF]">× {it.qty}</span>
                        {it.preorder_type === "allocation-deposit" && (
                          <span className="text-[10px] text-[#C9A227] font-mono-ps tracking-widest uppercase ml-2">ACCONTO {it.deposit_percent}%</span>
                        )}
                      </span>
                      <span className="font-mono-ps shrink-0">€ {(it.line_today ?? it.line_total ?? 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <OrderDocuments orderId={o.order_id} />
              </div>
            ))}
          </div>
        )}

        {tab === "vouchers" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vouchers.length === 0 && <Empty text="Nessun buono negozio disponibile." />}
            {vouchers.map((v) => (
              <div key={v.code} data-testid={`my-voucher-${v.code}`} className={`border rounded-sm p-5 bg-[#0b0b0d] ${isExpiringSoon(v) ? "border-emerald-400 shadow-[0_0_24px_rgba(52,211,153,0.15)]" : "border-[#C9A227]/30"}`}>
                <div className="flex justify-between items-center">
                  <div className="eyebrow">Buono negozio</div>
                  {isExpiringSoon(v) && (
                    <span data-testid={`my-voucher-expiring-${v.code}`} className="text-[10px] font-mono-ps uppercase tracking-widest text-emerald-400 border border-emerald-400/50 px-2 py-0.5 rounded-sm">
                      Scade tra {daysLeft(v.expires_at)} gg
                    </span>
                  )}
                </div>
                <div className="font-mono-ps text-lg text-[#C9A227] mt-2">{v.code}</div>
                <div className="font-display text-2xl font-bold text-white mt-2">€ {v.balance.toFixed(2)}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">su € {v.initial_amount.toFixed(2)} • scade il {new Date(v.expires_at).toLocaleDateString("it-IT")}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "wishlist" && (
          <div>
            {wl.length === 0 ? <Empty text="La tua wishlist è vuota." /> : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {wl.map((p) => <ProductCard key={p.product_id} product={p} />)}
              </div>
            )}
          </div>
        )}

        {tab === "addresses" && <Empty text="Nessun indirizzo salvato. Aggiungine uno al prossimo checkout." />}
        {tab === "profile" && (
          <div className="border border-[#C9A227]/15 rounded-sm p-6 bg-[#0b0b0d] max-w-lg">
            <Field label="Nome" value={user.name} />
            <Field label="Email" value={user.email} />
            <Field label="Provider" value={user.provider || "local"} />
            {user.is_admin && <Field label="Ruolo" value="Admin" highlight />}
          </div>
        )}
      </div>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="border border-[#C9A227]/15 rounded-sm p-10 text-center text-[#9CA3AF]">{text}</div>
);
const Field = ({ label, value, highlight }) => (
  <div className="py-3 border-b border-[#C9A227]/10 last:border-0 flex justify-between">
    <span className="text-xs font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</span>
    <span className={`text-sm ${highlight ? "text-[#C9A227] font-bold" : "text-white"}`}>{value}</span>
  </div>
);
